import { Col, Row } from 'antd';
import { noop, omit } from 'lodash';
import { FC, useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import { Scale, ScaleVisualization } from '~src/interfaces/scale.interface';
import { ContentLocalesI } from '~src/interfaces/session.types';
import {
  EditLabelsFormData,
  EditMcqOptionsFormData,
  editScale,
  getScale,
} from '~src/modules/scales/actions/scaleActions';
import { StoreI } from '~src/rootReducer';
import { routes } from '~src/routingConfig';
import {ApiReduxResp, isSuccess, ThunkMWDispatch} from '~src/services/apiMiddleware';
import { getCompanyContentLocale } from '~src/services/session';
import { translateContent } from '~src/services/translationServices';
import { validatorMaxLength40, validatorMaxLength80, validatorRequired } from '~src/services/validators';
import ScaleComponent  from '~src/shared/features/scale/Scale';
import ScaleBoxItem from '~src/shared/features/scale/scaleBoxItem/ScaleBoxItem';
import StarsList, { ListClass } from '~src/shared/features/scale/starsList/StarsList';
import TextBtnPageFooter from '~src/shared/footers/textBtnPageFooter/TextBtnPageFooter';
import Checkbox from '~src/shared/form/Checkbox';
import { SectionOpt, TranslatableField } from '~src/shared/form/translatableSectionForm/interfaces';
import TranslatableSectionFormRaw, {
  FormValuesI,
  TSFormData,
} from '~src/shared/form/translatableSectionForm/translatableSectionFormRaw';
import TileHeader from '~src/shared/headers/tileHeader/TileHeader';
import PageLoader from '~src/shared/pageLoader/PageLoader';
import arrayMutators from '~src/shared/reactFinalForm/arrayMutators';
import { composeValidators } from '~src/shared/reactFinalForm/validators';
import SvgIcon from '~src/shared/SvgIcon';
import { MainContainerAnimation } from '~src/styles/animations';
import { background, baseBlue, orderedColorsSetsReversed } from '~src/styles/colors';
import { PartialBy } from '~src/types';
import { clearContentLocalesI, mapKeyToDataValue, trimValues } from '~src/utils';

import TranslatableDynamicFormRaw, {
  TranslatableDynamicFormFormData,
} from '~src/modules/scales/components/add/translatableDynamicForm/TranslatableDynamicFormRaw';
import { ErrorBox } from '~src/shared/form/shared.sc';
import * as labelsFieldsData from './editStarsNpsLabels/fieldsData';
import * as SC from './scaleDetails.sc';
import ScaleHeader from './ScaleHeader';
import {getCatchSubmitAnyError} from "~src/shared/reactFinalForm/catchSubmitAnyError";
import {submitErrorToFormError} from "~src/shared/reactFinalForm/submitErrorToFormError";

type OwnProps = RouteComponentProps<{ scaleId: string }>;

interface ConnectedFields {
  [key: string]: ContentLocalesI;
}

export interface FormTransValues {
  translations: ContentLocalesI[];
  isMultipleChoiceEnabled?: boolean;
}

type FieldProps = Pick<TranslatableField, 'validator' | 'translationValidator'> & {
  value: { [key: string]: string };
};
type mappedFieldT = (FieldProps & labelsFieldsData.StarLabel) | (FieldProps & labelsFieldsData.NpsLabel);

type FieldArr = ContentLocalesI[];

interface StateProps {
  scale: Scale | null;
  scaleType: ScaleVisualization | null;
  defaultContentLocale: string;
  scaleId: string;
  loadingScale: boolean;
  loadingEdit: boolean;
}

interface DispatchProps {
  getScale(id: string): ApiReduxResp<Scale>;
  editScale(
    scaleId: string,
    formData: EditLabelsFormData | EditMcqOptionsFormData,
  ): ApiReduxResp<Scale>;
}

type Props = OwnProps & StateProps & DispatchProps;

const ScaleDetails: FC<Props> = (props) => {
  const [t] = useTranslation();
  const [isWithLabels, setIsWithLabels] = useState<boolean>(
    Boolean(props.scale && props.scale.labels.length > 0),
  );

  const [isSavingLabels, setIsSavingLabels] = useState(false);

  function labelsToCustomLabels(scaleLabels: ContentLocalesI[]): ContentLocalesI[] {
    if (scaleLabels.length === 1 && scaleLabels[0][props.defaultContentLocale] === '...') {
      scaleLabels[0][props.defaultContentLocale] = '';
    }

    return scaleLabels.slice().reverse();
  }

  useEffect(() => {
    void props.getScale(props.scaleId).then((res) => {
      if (isSuccess(res)) {
          setIsWithLabels(Boolean(res.response.data.labels.length));
      }
    });
  }, []);

  const mapLabelsForPreview = (
    connectedFields: ConnectedFields | ContentLocalesI[],
    previewLocale: string,
  ): ContentLocalesI[] => {
    return Object.values(connectedFields)
      .reverse()
      .map(label => (label[previewLocale] ? { [props.defaultContentLocale]: label[previewLocale] } : {}));
  };

  const doSubmitCustomLabels = (values: FormTransValues) => {
    const mappedValues = Object.values(values.translations);
    mappedValues.reverse();

    return props.editScale(props.scaleId, { labels: mappedValues }).then((saveResponse) => {
      return props.getScale(props.scaleId).then(() => saveResponse);
    });
  };

  const onSubmitCustomLabels = (values: TranslatableDynamicFormFormData) => {
    const adjustedValues = trimValues(values) as PartialBy<
      TranslatableDynamicFormFormData,
      'selectedContentLocale' | 'isTranslationBoxVisible'
    >;
    delete adjustedValues.selectedContentLocale;
    delete adjustedValues.isTranslationBoxVisible;
    setIsSavingLabels(true);

    return doSubmitCustomLabels(adjustedValues)
      .then(submitErrorToFormError)
      .catch(getCatchSubmitAnyError('ScaleDetails', 'error saving custom labels'))
      .finally(() => {
        setIsSavingLabels(false);
      });
  };

  const doSubmitMcqOptions = async (values: FormTransValues) => {
    const mappedValues = Object.values(values.translations);
    mappedValues.reverse();

    return props
      .editScale(props.scaleId, {
        multipleChoiceOptions: mappedValues,
        isMultipleChoiceEnabled: values.isMultipleChoiceEnabled || false,
      })
      .then((saveResponse) => {
        return props.getScale(props.scaleId).then(() => saveResponse);
      });
  };

  const onSubmitMcqOptions = async (values: TranslatableDynamicFormFormData) => {
    const adjustedValues = trimValues(values) as PartialBy<
      TranslatableDynamicFormFormData,
      'selectedContentLocale' | 'isTranslationBoxVisible'
    >;
    delete adjustedValues.selectedContentLocale;
    delete adjustedValues.isTranslationBoxVisible;
    setIsSavingLabels(true);

    return doSubmitMcqOptions(adjustedValues)
      .then(submitErrorToFormError)
      .catch(getCatchSubmitAnyError('ScaleDetails', 'error saving MCQ options'))
      .finally(() => {
        setIsSavingLabels(false);
      });
  };

  function isContentLocale(value: string|boolean|ContentLocalesI): value is ContentLocalesI {
    return (Boolean(value) && typeof value === 'object');
  }

  const submitStarsNpsLabels = (values: FormValuesI) => {
    let mappedValues: ContentLocalesI[] = Object.values(values).reverse().filter(isContentLocale);
    if (!isWithLabels) {
      mappedValues = mappedValues.map(clearContentLocalesI);
    }

    return props.editScale(props.scaleId, { labels: mappedValues });
  };

  const starsNpsLabelsGetInitialValues = (
    sections: SectionOpt[],
    defaultContentLocale: string,
  ): TSFormData => {
    const allSections = sections.reduce((acc: TranslatableField[], section) => {
      acc = [...acc, ...section.fields];
      return acc;
    }, []);
    const initFrmVals = mapKeyToDataValue(allSections, 'name', 'value');
    return {
      ...initFrmVals,
      selectedContentLocale: defaultContentLocale === 'en_US' ? 'de_DE' : 'en_US',
      isTranslationBoxVisible: false,
    };
  };

  const translatableDynamicFormGetInitialValues = (
    fieldArr: FieldArr,
    selectedContentLocale: string,
  ): TranslatableDynamicFormFormData => {
    return {
      translations: [...fieldArr],
      selectedContentLocale,
      isTranslationBoxVisible: false,
      isMultipleChoiceEnabled: false,
    };
  };

  const starsNpsLabelsDoSubmit = async (values: TSFormData) => {
    const adjustedValues: FormValuesI &
      Partial<Pick<TSFormData, 'selectedContentLocale' | 'isTranslationBoxVisible'>> = trimValues(values);
    delete adjustedValues.selectedContentLocale;
    delete adjustedValues.isTranslationBoxVisible;
    const res = await submitStarsNpsLabels(adjustedValues);
    return submitErrorToFormError(res);
  };

  const getNpsLabelSections = (scaleType: string, scaleLabels: ContentLocalesI[]) => {
    const fieldsData = labelsFieldsData[scaleType === 'nps' ? 'npsLabelsData' : 'starsLabelsData'];
    const editScaleFieldsReversed = [...scaleLabels].reverse();
    const fields = (fieldsData as Array<labelsFieldsData.StarLabel | labelsFieldsData.NpsLabel>).reduce(
      (acc: mappedFieldT[], field, i) => {
        acc.push({
          ...field,
          value: editScaleFieldsReversed[i] || {},
          validator: validatorMaxLength40,
          translationValidator: validatorMaxLength40,
        });
        return acc;
      },
      [],
    );

    return [
      {
        name: 'labels',
        fields,
      },
    ];
  };

  const withScaleHeader = (inner: JSX.Element, labels: ContentLocalesI[]) => {
    const isCountAboveFour =
      labels.length > 4 &&
      (scale.visualization === ScaleVisualization.Labels ||
        scale.visualization === ScaleVisualization.McqOptions);
    return (
      <>
        <ScaleHeader history={props.history} scale={scale} />
        <MainContainerAnimation>
          <SC.ScaleTileContainer $isCountAboveFour={isCountAboveFour}>
            <SC.ScaleInfo $isCountAboveFour={isCountAboveFour}>
              <TileHeader
                title={t('x.scale.details.preview_title')}
                titleKey="x.scale.details.preview_title"
                tooltip={translateContent(scale.description)}
              />
              <ScaleComponent
                t={t}
                rating={null}
                type={scale.visualization}
                labels={labels}
                inContainer
                isPreview
                isScaleDetails
                isMultipleChoiceEnabled={scale.isMultipleChoiceEnabled}
              />
            </SC.ScaleInfo>
            {inner}
          </SC.ScaleTileContainer>
        </MainContainerAnimation>
      </>
    );
  };

  const npsLabelSections =
    props.scale && props.scale.visualization !== ScaleVisualization.Labels
      ? getNpsLabelSections(props.scale.visualization, props.scale.labels)
      : [];

  console.log({
    npsLabelSections,
    initial: starsNpsLabelsGetInitialValues(npsLabelSections, props.defaultContentLocale),
  });

  const editStarsNpsLabels = () => {
    return (
      <Form
        initialValues={starsNpsLabelsGetInitialValues(npsLabelSections, props.defaultContentLocale)}
        keepDirtyOnReinitialize
        mutators={arrayMutators}
        onSubmit={starsNpsLabelsDoSubmit}
      >
        {({ handleSubmit, values, form }) => {
          // @ts-expect-error need to exclude boolean|string from possible values
          const labels: Record<string, ContentLocalesI> = omit(
            values,
            'selectedContentLocale',
            'isTranslationBoxVisible',
          );

          const onToggleWithLabels = (newValue: boolean) => {
            setIsWithLabels(newValue);
            if (!newValue) {
              Object.keys(labels).forEach((key) => {
                form.change(key, {});
              });
            } else {
              form.reset();
            }
          };

          const previewLocale =
            values.isTranslationBoxVisible && values.selectedContentLocale
              ? values.selectedContentLocale
              : props.defaultContentLocale;

          return withScaleHeader(
            <SC.ComponentContainer className={isWithLabels ? 'with-text' : ''}>
              <SC.ScaleRow>
                <Col md={6} sm={8}>
                  <SC.CheckboxContainer>
                    <Checkbox
                      onChange={onToggleWithLabels}
                      val="withLabels"
                      labelKey="x.scale.details.add_mouse_over_text"
                      label={t<string>('x.scale.details.add_mouse_over_text')}
                      id="checkboxWithLabels"
                      isChecked={isWithLabels}
                    />
                  </SC.CheckboxContainer>
                  <SC.NpsStarsScaleStatesContainer>
                    {npsLabelSections[0].fields.map(({ scaleState, name }) => (
                      <SC.ScaleStateBox key={name}>
                        {props.scale && props.scale.visualization === ScaleVisualization.Nps ? (
                          <ScaleBoxItem
                            type={ScaleVisualization.Nps}
                            colorNumber={scaleState}
                            withHover
                            selected={false}
                          />
                        ) : (
                          <StarsList starsLength={scaleState} listCls={ListClass.goldFilled}/>
                        )}
                      </SC.ScaleStateBox>
                    ))}
                  </SC.NpsStarsScaleStatesContainer>
                </Col>
                <Col md={18} sm={16}>
                  <div className="form-container">
                    <TranslatableSectionFormRaw
                      t={t}
                      form="editScaleDetails"
                      sections={npsLabelSections}
                      onSubmit={submitStarsNpsLabels}
                      loadingSave={props.loadingEdit}
                      onCancel={noop}
                      showText={isWithLabels}
                      handleSubmit={handleSubmit}
                    />
                  </div>
                </Col>
              </SC.ScaleRow>
            </SC.ComponentContainer>,
            mapLabelsForPreview(labels, previewLocale),
          );
        }}
      </Form>
    );
  };

  const editCustomLabels = (key: string) => {
    const fieldsOpts = {
      label: 'x.scale.edit_labels.field_label',
      labelLow: 'x.scale.edit_labels.field_label_low',
      labelHigh: 'x.scale.edit_labels.field_label_high',
      validator: composeValidators([validatorRequired, validatorMaxLength80]),
      translationValidator: validatorMaxLength80,
    };

    return (
      <Form
        key={key}
        initialValues={translatableDynamicFormGetInitialValues(
          labelsToCustomLabels((props.scale && props.scale.labels) || []),
          props.defaultContentLocale === 'en_US' ? 'de_DE' : 'en_US',
        )}
        keepDirtyOnReinitialize
        mutators={arrayMutators}
        onSubmit={onSubmitCustomLabels}
      >
        {({
          handleSubmit,
          form: {
            mutators: { remove, unshift },
          },
          submitError,
          values,
        }) => {
          const translations: ContentLocalesI[] = values.translations;
          const removeRow = (i: number) => {
            remove('translations', i);
          };

          const addRow = () => {
            const value: ContentLocalesI = { [props.defaultContentLocale]: '' };
            if (values.selectedContentLocale) {
              value[values.selectedContentLocale] = '';
            }

            unshift('translations', value);
          };

          const previewLocale =
            values.isTranslationBoxVisible && values.selectedContentLocale
              ? values.selectedContentLocale
              : props.defaultContentLocale;

          return withScaleHeader(
            <SC.ComponentContainer>
              <SC.AddLabelLnk onClick={addRow} $disabled={translations.length >= 10}>
                + {t('x.scale.edit_labels.add_label_button')}
              </SC.AddLabelLnk>

              <Row gutter={30}>
                <Col span={6}>
                  <SC.CustomScaleStatesContainer>
                    {translations &&
                      translations.length &&
                      translations.map((labelTranslations, i, fieldsArr) => {
                        const fieldRemoveRow = () => removeRow(i);
                        return (
                          <SC.ScaleStateBox key={i}>
                            <SC.StyledTextBoxWithTooltip
                              text={labelTranslations[previewLocale] || '...'}
                              backgroundColor={orderedColorsSetsReversed[fieldsArr.length][i]}
                            />
                            {fieldsArr.length > 1 && (
                              <SC.DelBtn onClick={fieldRemoveRow}>
                                <SvgIcon name="iconCrossInCircle" width={15} />
                              </SC.DelBtn>
                            )}
                          </SC.ScaleStateBox>
                        );
                      })}
                  </SC.CustomScaleStatesContainer>
                </Col>
                <Col span={18}>
                  <TranslatableDynamicFormRaw
                    t={t}
                    form="editScaleDetails"
                    leftColSize={9}
                    fieldsOpts={fieldsOpts}
                    loadingSave={props.loadingEdit}
                    handleSubmit={handleSubmit}
                  />
                </Col>
              </Row>
              {submitError ? <ErrorBox>{t(submitError)}</ErrorBox> : null}
            </SC.ComponentContainer>,
            mapLabelsForPreview(translations, previewLocale),
          );
        }}
      </Form>
    );
  };

  const editMcqOptions = (key: string) => {
    const fieldsOpts = {
      label: 'x.scale.edit_labels.field_label',
      validator: composeValidators([validatorRequired, validatorMaxLength80]),
      translationValidator: validatorMaxLength80,
    };
    return (
      <Form
        key={key}
        initialValues={{
          ...translatableDynamicFormGetInitialValues(
            labelsToCustomLabels((props.scale && props.scale.multipleChoiceOptions) || []),
            props.defaultContentLocale === 'en_US' ? 'de_DE' : 'en_US',
          ),
          isMultipleChoiceEnabled: props.scale?.isMultipleChoiceEnabled,
        }}
        keepDirtyOnReinitialize
        mutators={arrayMutators}
        onSubmit={onSubmitMcqOptions}
      >
        {({
          handleSubmit,
          form: {
            mutators: { remove, unshift },
          },
          submitError,
          values,
        }) => {
          const translations: ContentLocalesI[] = values.translations;
          const removeRow = (i: number) => {
            remove('translations', i);
          };

          const addRow = () => {
            const value: ContentLocalesI = { [props.defaultContentLocale]: '' };
            if (values.selectedContentLocale) {
              value[values.selectedContentLocale] = '';
            }

            unshift('translations', value);
          };

          const previewLocale =
            values.isTranslationBoxVisible && values.selectedContentLocale
              ? values.selectedContentLocale
              : props.defaultContentLocale;

          return withScaleHeader(
            <SC.ComponentContainer>
              <SC.AddLabelLnk onClick={addRow} $disabled={translations.length >= 20}>
                + {t('x.scale.edit.mcq.options.button.label')}
              </SC.AddLabelLnk>

              <Row gutter={30}>
                <Col span={6}>
                  <SC.CustomScaleStatesContainer>
                    {translations &&
                      translations.length &&
                      translations.map((labelTranslations, i, fieldsArr) => {
                        const fieldRemoveRow = () => removeRow(i);
                        return (
                          <SC.ScaleStateBox key={i}>
                            <ScaleBoxItem
                              selected={false}
                              isPreview
                              withHover={false}
                              type={ScaleVisualization.McqOptions}
                              showCheckboxOptions={values.isMultipleChoiceEnabled}
                              scaleLength={fieldsArr.length}
                              reversed
                              colorNumber={i}
                              label={labelTranslations[previewLocale]}
                            />
                            {fieldsArr.length > 1 && (
                              <SC.DelBtn onClick={fieldRemoveRow}>
                                <SvgIcon name="iconCrossInCircle" width={15} />
                              </SC.DelBtn>
                            )}
                          </SC.ScaleStateBox>
                        );
                      })}
                  </SC.CustomScaleStatesContainer>
                </Col>
                <Col span={18}>
                  <TranslatableDynamicFormRaw
                    t={t}
                    isMcq
                    form="editScaleDetails"
                    leftColSize={9}
                    fieldsOpts={fieldsOpts}
                    loadingSave={props.loadingEdit}
                    handleSubmit={handleSubmit}
                  />
                </Col>
              </Row>
              {submitError ? <ErrorBox>{t(submitError)}</ErrorBox> : null}
            </SC.ComponentContainer>,
            mapLabelsForPreview(translations, previewLocale),
          );
        }}
      </Form>
    );
  };

  if ((!isSavingLabels && props.loadingScale) || !props.scale) {
    return <PageLoader secondColor={background} color={baseBlue.a10} />;
  }

  const scale = props.scale;

  let visualization;
  switch (scale.visualization) {
    case ScaleVisualization.Labels:
      visualization = editCustomLabels(scale.id);
      break;
    case ScaleVisualization.McqOptions:
      visualization = editMcqOptions(scale.id);
      break;
    default:
      visualization = editStarsNpsLabels();
  }

  return (
    <SC.ScalePageContainer>
      {visualization}
      <TextBtnPageFooter
        t={t}
        infoKey="x.scale.details.footer.info_text"
        btnKey="x.scale.details.footer.button_text"
        linkUrl={routes.contentTopics.topics}
      />
    </SC.ScalePageContainer>
  );
};

const mapStateToProps = (state: StoreI, ownProps: OwnProps): StateProps => {
  return {
    defaultContentLocale: getCompanyContentLocale(state),
    scaleId: ownProps.match.params.scaleId,
    scale: state.scales.item,
    scaleType: state.scales.item && state.scales.item.visualization,
    loadingScale: state.scales.loadingScale,
    loadingEdit: state.scales.loadingEdit,
  };
};

const mapDispatchToProps = (dispatch: ThunkMWDispatch): DispatchProps => ({
  getScale: (id: string) => dispatch(getScale(id)),
  editScale: (scaleId: string, formData: EditLabelsFormData | EditMcqOptionsFormData) =>
    dispatch(editScale(scaleId, formData)),
});

// @ts-expect-error ThunkMWDispatch is different from what React expects
export default connect(mapStateToProps, mapDispatchToProps)(ScaleDetails);
